/* Prices, written the way the market writes them.
 *
 * THE RULE: the shape of an amount is the market's, not ours. Egypt's row
 * carries `currency_format = "{n} ج.م"`; Saudi's carries "{n} ر.س". Neither
 * string is composed here from a symbol and a guess at which side it goes.
 *
 * THE ONE PLACE LOCALE DECIDES: English. `currency_format` holds one value
 * per market and it is the Arabic one, because that is the market's own
 * writing. The design's English cards read "EGP 9.2M" -- code first, amount
 * second -- so in English the ISO code from the same row is used with that
 * order. This is a locale rule in code, not a currency hardcoded: nothing
 * here knows what EGP is, only where it goes.
 *
 * DIGITS ARE WESTERN in both locales, by Bassem's ruling, against the design,
 * which writes «٩٫٢ مليون». Logged in docs/DEVIATIONS.md.
 */

import type { Locale } from "@/i18n/strings";
import type { Market } from "@/api/cms";
import { formatNumber } from "@/lib/format";
import { fill, type V3Strings } from "./strings";

/** "9.2 مليون" / "9.2M" — the number and its magnitude word, no currency. */
function compactAmount(value: number, locale: Locale, s: V3Strings): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    const n = value / 1_000_000;
    // One decimal, and not a trailing ".0" -- the design writes 24.5M and 9.2M
    // but plain 12M would read as 12.0M for no reason.
    const text = formatNumber(Math.round(n * 10) / 10, locale);
    return locale === "ar" ? `${text} ${s.listing.million}` : `${text}${s.listing.million}`;
  }
  if (abs >= 1_000) {
    const n = Math.round(value / 1_000);
    const text = formatNumber(n, locale);
    return locale === "ar" ? `${text} ${s.listing.thousand}` : `${text}${s.listing.thousand}`;
  }
  return formatNumber(value, locale);
}

export type PriceOptions = {
  /** Rent gets the "/ mo" suffix the design puts on rental cards. */
  perMonth?: boolean;
  /** Full precision instead of the card's compact form (used on detail). */
  exact?: boolean;
  /** The row's own ISO code, used when the market does not supply one. */
  currency?: string | null;
};

/**
 * A listing price as the design writes it: "9.2 مليون ج.م", "EGP 35K / mo".
 * Returns "" for a missing price -- the caller decides what absence looks
 * like, because a dash in a price slot reads as "free" to some people.
 */
export function marketPrice(
  value: number | null | undefined,
  market: Market | null,
  locale: Locale,
  s: V3Strings,
  opts: PriceOptions = {},
): string {
  if (value == null) return "";
  const amount = opts.exact ? formatNumber(value, locale) : compactAmount(value, locale, s);
  const withCurrency = writeAmount(amount, market, locale, opts.currency);
  return opts.perMonth ? `${withCurrency} ${s.listing.perMonth}` : withCurrency;
}

/**
 * Put an already-formatted number into the market's currency shape.
 *
 * `currency_display` on the market row holds BOTH locales, each with its own
 * format and {n} where the number goes, so this function chooses nothing: it
 * fills in a template the market supplied. That is Bassem's ruling — no
 * currency literal in code, and no rule in code about which side of the
 * number the currency belongs on.
 *
 * The two fallbacks below are for markets nobody has filled in yet, in order:
 * `currency_format` from migration 004, then the ISO code off the row being
 * rendered. If none of the three names a currency the amount renders bare —
 * a number with no currency beside it is not a shorter price, it is a
 * different claim, and guessing one would be the worse answer.
 */
export function writeAmount(
  amount: string, market: Market | null, locale: Locale, fallbackCurrency?: string | null,
): string {
  const display = market?.currencyDisplay?.[locale];
  if (display?.format) return fill(display.format, { n: amount });
  if (display?.label) return `${amount} ${display.label}`;
  if (market?.currencyFormat) return fill(market.currencyFormat, { n: amount });
  const symbol = market?.currencySymbol ?? market?.currency ?? fallbackCurrency ?? null;
  return symbol ? `${amount} ${symbol}` : amount;
}

/** The currency on its own, as this market writes it in this locale. */
export function currencyLabel(market: Market | null, locale: Locale): string {
  return market?.currencyDisplay?.[locale]?.label
    ?? (locale === "en" ? market?.currency : market?.currencySymbol)
    ?? market?.currency ?? "";
}

/** The label under a plan price: «ج.م / شهر» / "EGP / month". */
export function currencyPerMonth(
  market: Market | null, locale: Locale, s: V3Strings,
): string {
  return [currencyLabel(market, locale), s.plan.perMonth].filter(Boolean).join(" ");
}
